import { fmtDateTimeParis } from "../../lib/date";

// Small stat tile used on the dashboard header and in some panels.
export function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`stat-${label}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="font-display text-3xl mt-1 gold-text">{value}</div>
    </div>
  );
}

export function DataTable({ rows, cols, testid }) {
  if (rows.length === 0) return <div className="text-muted-foreground">Aucune donnée.</div>;
  return (
    <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="border-b border-border">
          <tr>
            {cols.map(([k, l]) => (
              <th key={k} className="text-left p-4 text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-border/50 last:border-0">
              {cols.map(([k]) => (
                <td key={k} className="p-4">{formatVal(r[k], k)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function formatVal(v, key) {
  if (v == null) return "—";
  if (key === "created_at" || key === "date" || key === "subscribed_at") {
    try { return new Date(v).toLocaleDateString("fr-FR"); } catch { return String(v); }
  }
  if (key === "total_price") return `${Number(v).toFixed(2)} €`;
  return String(v);
}

// Delivery / open status pill for email notifications (Resend events).
export function NotificationPill({ role, notif }) {
  if (!notif) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/5 px-2 py-0.5 text-amber-300"
        data-testid={`notif-${role.toLowerCase()}-none`}
        title="Aucun envoi enregistré — cliquer sur Renvoyer pour tenter"
      >
        ⚠ {role} : jamais envoyé
      </span>
    );
  }
  const map = {
    sent:         { label: "Envoyé",       cls: "border-sky-400/40 bg-sky-400/10 text-sky-300",     icon: "✉" },
    delivered:    { label: "Livré",        cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300", icon: "✓" },
    opened:       { label: "Ouvert",       cls: "border-primary/50 bg-primary/10 text-primary",     icon: "👁" },
    clicked:      { label: "Cliqué",       cls: "border-primary/50 bg-primary/10 text-primary",     icon: "🖱" },
    bounced:      { label: "Rejeté",       cls: "border-red-500/50 bg-red-500/10 text-red-400",     icon: "✗" },
    complained:   { label: "Spam",         cls: "border-red-500/50 bg-red-500/10 text-red-400",     icon: "!" },
    failed:       { label: "Échec",        cls: "border-red-500/50 bg-red-500/10 text-red-400",     icon: "✗" },
    failed_send:  { label: "Non envoyé",   cls: "border-red-500/50 bg-red-500/10 text-red-400",     icon: "✗" },
    delayed:      { label: "Différé",      cls: "border-amber-400/40 bg-amber-400/10 text-amber-300", icon: "⏱" },
  };
  const meta = map[notif.status] || { label: notif.status, cls: "border-border bg-card text-muted-foreground", icon: "•" };
  const ts = notif.delivered_at || notif.opened_at || notif.bounced_at || notif.sent_at || notif.updated_at;
  const when = ts ? fmtDateTimeParis(ts) : "";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 ${meta.cls}`}
      title={`${notif.email || ""}${when ? ` · ${when}` : ""}${notif.resend_id ? `\nID Resend: ${notif.resend_id}` : ""}`}
      data-testid={`notif-${role.toLowerCase()}-${notif.status}`}
    >
      <span aria-hidden>{meta.icon}</span>
      <span>{role} : {meta.label}</span>
      {when && <span className="text-[10px] opacity-70">· {when}</span>}
    </span>
  );
}

// Reusable filter pill for tab panels (bookings / orders / tables).
export function FilterPill({ value, current, label, count, onClick, danger }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors ${
        active
          ? (danger ? "bg-rose-400 text-card border-rose-400" : "bg-primary text-primary-foreground border-primary")
          : `border-border bg-card hover:border-primary/50 text-muted-foreground ${danger ? "hover:text-rose-400 hover:border-rose-400/50" : ""}`
      }`}
      data-testid={`filter-${value}`}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );
}
